const COMPARE_SCHEMA_VERSION = 'all-products-v4';
const COMPARE_MAX_PRODUCTS = 4;
const COMPARE_FETCH_TIMEOUT = 15000;


class CompareUtil {
  /**
   * Returns an array of the selected compare products from localStorage
   * @returns {Array}
   */
  static getSelectedProducts() {
    const compareProducts = theme.storageUtil.get('compare-products', true);
    const normalized = CompareUtil.validateSelectedProducts(compareProducts);
    if (!normalized) {
      theme.storageUtil.set('compare-products', []);
      return [];
    }
    return normalized;
  }

  /**
   * Treats localStorage as an untrusted boundary.
   * @param {*} compareProducts - Persisted value.
   * @returns {Array|null} validated items or null when the whole selection is stale/malformed.
   */
  static validateSelectedProducts(compareProducts) {
    if (!Array.isArray(compareProducts) || compareProducts.length > COMPARE_MAX_PRODUCTS) return null;

    const ids = new Set();
    const urls = new Set();
    let group = null;
    const normalized = [];
    for (const product of compareProducts) {
      if (!product || typeof product !== 'object' || Array.isArray(product)
        || typeof product.id !== 'string' || !/^\d{6,20}$/.test(product.id)
        || typeof product.url !== 'string'
        || typeof product.group !== 'string' || !/^[a-z0-9][a-z0-9-]{0,79}$/.test(product.group)
        || product.schema !== COMPARE_SCHEMA_VERSION
        || ids.has(product.id)) return null;

      let productUrl;
      try {
        productUrl = new URL(product.url, window.location.origin);
      } catch (_error) {
        return null;
      }
      const pathParts = productUrl.pathname.split('/').filter(Boolean);
      const productIndex = pathParts.indexOf('products');
      if (productUrl.origin !== window.location.origin
        || productIndex < 0 || productIndex !== pathParts.length - 2
        || !/^[a-z0-9][a-z0-9-]*$/.test(pathParts[productIndex + 1])) return null;

      const canonicalPath = productUrl.pathname.replace(/\/+$/, '');
      if (urls.has(canonicalPath)) return null;
      if (group && group !== product.group) return null;
      group = product.group;
      ids.add(product.id);
      urls.add(canonicalPath);
      normalized.push({ id: product.id, url: canonicalPath, group: product.group, schema: COMPARE_SCHEMA_VERSION });
    }
    return normalized;
  }

  /**
   * Sets an array of the selected compare products to localStorage
   * @param {Array} compareProducts - An array of product objects to compare
   */
  static setSelectedProducts(compareProducts) {
    theme.storageUtil.set('compare-products', compareProducts);
    document.dispatchEvent(new CustomEvent('on:compare:updated', {
      detail: { count: compareProducts.length }
    }));
  }

  static getCurrentCollectionGroup() {
    const group = document.querySelector('[data-compare-collection-group]')?.dataset.compareCollectionGroup || '';
    return /^[a-z0-9][a-z0-9-]{0,79}$/.test(group) ? group : '';
  }

  static syncCollectionScope() {
    const currentGroup = CompareUtil.getCurrentCollectionGroup();
    if (!currentGroup) return;
    const selectedProducts = CompareUtil.getSelectedProducts();
    if (selectedProducts.length && selectedProducts[0].group !== currentGroup) CompareUtil.clearCompare(true);
  }

  /**
   * Adds a product id and url to the compare array in localStorage
   * @param {string} productId - the product id
   * @param {string} productUrl - the product url
   * @param {boolean} updateDom - whether the dom should be updated or not
   */
  static addToCompare(productId, productUrl, productGroup, updateDom) {
    const compareProducts = CompareUtil.getSelectedProducts();
    if (!compareProducts.some((product) => product.id === productId)) {
      compareProducts.push({ id: productId, url: productUrl, group: productGroup, schema: COMPARE_SCHEMA_VERSION });
    }
    CompareUtil.setSelectedProducts(compareProducts);
    CompareUtil.updateCompareCounters(compareProducts.length);

    if (updateDom) {
      CompareUtil.updateCompareCheckboxes();
    }
  }

  /**
   * Removes a product from the compare array in localStorage
   * @param {string} productId - the product id
   * @param {boolean} updateDom - whether the dom should be updated or not
   */
  static removeFromCompare(productId, updateDom) {
    let compareProducts = CompareUtil.getSelectedProducts();
    if (compareProducts) {
      compareProducts = compareProducts.filter((product) => product.id !== productId);
    }
    CompareUtil.setSelectedProducts(compareProducts);
    CompareUtil.updateCompareCounters(compareProducts.length);

    if (updateDom) {
      CompareUtil.updateCompareCheckboxes();

      // Remove the product from the compare basket
      theme.elementUtil.remove(document.getElementById(`compare-basket-${productId}`));

      // Remove the product from the compare modal
      theme.elementUtil.remove(document.querySelectorAll(`.compare-col--${productId}`));
    }
  }

  /**
   * Removes a product from the compare array in localStorage
   * @param {boolean} updateDom - whether the dom should be updated or not
   */
  static clearCompare(updateDom) {
    const compareProducts = CompareUtil.getSelectedProducts();
    CompareUtil.setSelectedProducts([]);
    CompareUtil.updateCompareCounters(0);
    if (updateDom) {
      CompareUtil.updateCompareCheckboxes();
      compareProducts.forEach((product) => {
        theme.elementUtil.remove(document.getElementById(`compare-basket-${product.id}`));
        theme.elementUtil.remove(document.querySelectorAll(`.compare-col--${product.id}`));
      });
    }
  }

  /**
   * Set whether the detected checkboxes should be checked or not
   */
  static updateCompareCheckboxes() {
    const compareProducts = CompareUtil.getSelectedProducts();
    document.querySelectorAll('.js-compare-checkbox').forEach((checkbox) => {
      checkbox.checked = compareProducts.filter(
        (product) => product.id === checkbox.dataset.productId
      ).length;
    });
  }

  /**
   * Returns a count of the number of currently in the compare basket
   * @returns {number}
   */
  static getCompareCount() {
    return CompareUtil.getSelectedProducts().length;
  }

  /**
   * Updates counters on the page with the count of products in the compare basket
   * @param {number} pCount - optionally can pass in the count of products to update
   */
  static updateCompareCounters(pCount) {
    const count = pCount ?? CompareUtil.getCompareCount();
    document.querySelectorAll('.js-compare-counter').forEach((counter) => {
      counter.innerText = count;
    });
  }
}

/* global SideDrawer */

if (!customElements.get('compare-drawer')) {
  class CompareDrawer extends SideDrawer {
    constructor() {
      super();
      this.launcher = document.getElementById('compare-drawer-open');
      this.openDrawerButton = document.querySelector('.js-open-compare-drawer');
      this.directCompareButton = document.querySelector('.js-trigger-compare-direct');
      this.compareBasketArea = this.querySelector('.js-compare-basket');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.doCompareButton = this.querySelector('.js-trigger-compare');
      this.clearButton = this.querySelector('.js-clear-compare');
      this.quickClearButton = document.querySelector('.js-clear-compare-quick');
      this.maxCompare = this.dataset.maxCompare;
      this.compareModal = document.querySelector('.js-compare-modal');
      this.statusRegion = document.querySelector('.js-compare-status');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.clickHandler);
      document.removeEventListener('change', this.changeHandler);
      document.removeEventListener('on:facet-filters:updated', CompareUtil.updateCompareCheckboxes);
      document.removeEventListener('on:compare:updated', this.compareUpdatedHandler);
      document.removeEventListener('on:hairone-color-tour:start', this.colorTourStartHandler);
    }

    init() {
      CompareUtil.syncCollectionScope();
      if (document.querySelector('[data-ho-color-tour]') && CompareUtil.getCompareCount()) CompareUtil.clearCompare(true);
      CompareUtil.updateCompareCheckboxes();
      CompareUtil.updateCompareCounters();
      this.toggleCompareButton();
      this.updateCompareActions();
      setTimeout(() => {
        this.launcher.removeAttribute('style');
        this.launcher.classList.add('transition-ready');
      }, 500);

      this.compareModal.querySelector('.details-modal').classList.remove('hidden');
    }

    bindEvents() {
      this.clickHandler = this.handleClick.bind(this);
      this.changeHandler = this.handleChange.bind(this);
      this.compareUpdatedHandler = () => {
        this.toggleCompareButton();
        this.updateCompareActions();
      };
      this.colorTourStartHandler = () => {
        if (!CompareUtil.getCompareCount()) return;
        CompareUtil.clearCompare(true);
        this.setStatus('Vergleich beendet, weil die Einkaufstour gestartet wurde.');
      };

      document.addEventListener('click', this.clickHandler);
      document.addEventListener('change', this.changeHandler);
      document.addEventListener('on:facet-filters:updated', CompareUtil.updateCompareCheckboxes);
      document.addEventListener('on:compare:updated', this.compareUpdatedHandler);
      document.addEventListener('on:hairone-color-tour:start', this.colorTourStartHandler);
      this.doCompareButton.addEventListener('click', this.handleDoCompareClick.bind(this));
      this.directCompareButton.addEventListener('click', this.handleDoCompareClick.bind(this));
      this.clearButton.addEventListener('click', this.handleClearCompareClick.bind(this));
      this.quickClearButton?.addEventListener('click', this.handleQuickClearCompareClick.bind(this));
    }

    /**
     * Updates the position of the 'Compare' fixed right-hand side button
     */
    toggleCompareButton() {
      this.launcher.classList.toggle('is-out', CompareUtil.getCompareCount() === 0);
    }

    /**
     * Keeps both compare CTAs honest: comparison requires at least two products.
     */
    updateCompareActions() {
      const disabled = CompareUtil.getCompareCount() < 2;
      this.doCompareButton.setAttribute('aria-disabled', disabled);
      this.doCompareButton.disabled = disabled;
      this.directCompareButton.setAttribute('aria-disabled', disabled);
      this.directCompareButton.disabled = disabled;
    }

    /**
     * Handles a 'click' event on open/close/remove from compare button
     * @param {object} evt - Event object
     */
    handleClick(evt) {
      if (evt.target.closest?.('.js-open-compare-drawer') === this.openDrawerButton) {
        if (evt.target.classList.contains('is-open')) {
          this.close();
        } else {
          this.open(evt.target);
        }
      } else if (evt.target.classList.contains('js-compare-basket-remove')) {
        CompareUtil.removeFromCompare(evt.target.dataset.productId.toString(), true);
        this.updateBasketState();
      } else if (this.overlay.classList.contains('js-compare-overlay') || evt.target.classList.contains('js-close-compare')) {
        this.close();
      }
    }

    /**
     * Handles a 'change' event for comparison checkboxes
     * @param {object} evt - Event object
     */
    handleChange(evt) {
      if (evt.target.classList.contains('js-compare-checkbox')) {
        const productData = evt.target.dataset;

        if (evt.target.checked) {
          const selectedProducts = CompareUtil.getSelectedProducts();
          const activeGroup = selectedProducts[0]?.group;
          if (!productData.compareGroup) {
            evt.target.checked = false;
            this.setStatus('Für dieses Produkt liegen noch keine belastbaren Vergleichsdaten vor.');
            return;
          }
          if (activeGroup && activeGroup !== productData.compareGroup) {
            evt.target.checked = false;
            this.setStatus('Bitte vergleiche nur Produkte derselben Produktart. Leere zuerst die bisherige Auswahl.');
            return;
          }
          if (CompareUtil.getCompareCount() < this.maxCompare) {
            CompareUtil.addToCompare(
              productData.productId.toString(),
              productData.productUrl,
              productData.compareGroup,
              false
            );
            this.setStatus(`${CompareUtil.getCompareCount()} von ${this.maxCompare} Produkten ausgewählt.`);
          } else {
            evt.target.checked = false;
            this.setStatus(theme.strings.compare.limit.replace('[quantity]', this.maxCompare));
          }
        } else {
          CompareUtil.removeFromCompare(productData.productId.toString(), false);
          this.setStatus(`${CompareUtil.getCompareCount()} von ${this.maxCompare} Produkten ausgewählt.`);
        }

        this.updateBasketState();

        this.toggleCompareButton();
      }
    }

    setStatus(message) {
      if (this.statusRegion) this.statusRegion.textContent = message;
    }

    /**
     * Handle click on the Compare button
     */
    handleDoCompareClick() {
      if (CompareUtil.getCompareCount() < 2) {
        this.setStatus('Wähle mindestens zwei Produkte zum Vergleichen aus.');
        return;
      }
      if (this.hasAttribute('open')) this.close();
      this.compareModal.dispatchEvent(new CustomEvent('on:product-compare-modal:open', { bubbles: true }));
    }

    /**
     * Handle click on the Clear button
     */
    handleClearCompareClick() {
      CompareUtil.clearCompare(true);
      this.setStatus('Vergleichsauswahl geleert.');
      this.close();
    }

    handleQuickClearCompareClick() {
      CompareUtil.clearCompare(true);
      this.setStatus('Vergleichsauswahl zurückgesetzt.');
      this.toggleCompareButton();
      this.updateCompareActions();
    }

    /**
     * Opens the compare drawer
     * @param {Element} opener - Element that triggered opening of the drawer
     */
    async open(opener) {
      opener.classList.add('is-open');
      this.overlay.classList.add('overlay--over-nav');
      this.overlay.classList.add('js-compare-overlay');
      super.open(opener);
      this.renderCompareBasket();
    }

    /**
     * Closes the compare drawer
     */
    close() {
      this.openDrawerButton.classList.remove('is-open');
      this.overlay.classList.remove('overlay--over-nav');
      this.overlay.classList.remove('js-compare-overlay');
      super.close();
    }

    /**
     * Enables/disables the 'Compare now' button based on number of products in compare basket
     */
    updateBasketState() {
      const compareCount = CompareUtil.getCompareCount();
      this.updateCompareActions();

      theme.elementUtil.remove(this.compareBasketArea.querySelector('.js-select-more'));
      theme.elementUtil.remove(this.compareBasketArea.querySelector('.js-select-one'));

      const continueButton = `<button class='js-close-compare link'>${theme.strings.compare.continue}</button>`;

      if (compareCount === 1) {
        this.compareBasketArea.innerHTML += `<div class='compare-prompt text-sm js-select-more'>${theme.strings.compare.more} ${continueButton}</div>`;
      } else if (compareCount === 0) {
        this.compareBasketArea.innerHTML += `<div class='compare-prompt text-sm js-select-one'>${theme.strings.compare.empty} ${continueButton}</div>`;
      }
    }

    /**
     * Fetches and renders the products in the compare basket
     */
    async renderCompareBasket() {
      const compareProducts = CompareUtil.getSelectedProducts();
      if (compareProducts) {
        // Reset the previous state if applicable
        this.loadingSpinner.removeAttribute('hidden');
        this.loadingSpinner.removeAttribute('aria-hidden');
        this.compareBasketArea.classList.remove('revealable--in');
        this.compareBasketArea.innerHTML = '';

        // Get the contents of the compare basket
        const basketResult = await CompareDrawer.getBasketProducts(compareProducts);
        if (basketResult.failedProducts.length) {
          CompareUtil.setSelectedProducts(basketResult.validProducts);
          this.setStatus(`${basketResult.failedProducts.length} nicht mehr verfügbares Produkt wurde aus dem Vergleich entfernt.`);
        }
        basketResult.html.forEach((compareProductHtml) => {
          this.compareBasketArea.innerHTML += compareProductHtml;
        });
        if (basketResult.failedProducts.length) {
          this.compareBasketArea.insertAdjacentHTML('beforeend', `<p class="compare-prompt text-sm" role="status">${basketResult.failedProducts.length === 1 ? 'Ein nicht mehr verfügbares Produkt wurde entfernt.' : 'Nicht mehr verfügbare Produkte wurden entfernt.'}</p>`);
        }

        // Timeout to trigger CSS transition
        setTimeout(() => {
          this.loadingSpinner.setAttribute('hidden', 'true');
          this.loadingSpinner.setAttribute('aria-hidden', 'true');
          this.compareBasketArea.classList.add('revealable--in');

          this.updateBasketState();
        }, 100);
      }
    }

    /**
     * Fetches an array of html for each of the compare products, using the section rendering API
     * @param {Array} compareProducts - An array of product objects to compare
     */
    static async getBasketProducts(compareProducts) {
      const settled = await Promise.allSettled(compareProducts.map(async (product) => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), COMPARE_FETCH_TIMEOUT);
        try {
          const response = await fetch(`${product.url}?sections=product-compare-basket`, {
            credentials: 'same-origin',
            signal: controller.signal
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const sections = await response.json();
          const html = sections['product-compare-basket'];
          if (typeof html !== 'string' || !html.includes(`compare-basket-${product.id}`)) throw new Error('Invalid compare basket section');
          const tmpl = document.createElement('template');
          tmpl.innerHTML = html;
          const renderedGroup = tmpl.content.querySelector('[data-compare-product-group]')?.dataset.compareProductGroup;
          if (!renderedGroup || renderedGroup !== product.group) throw new Error('Compare group mismatch');
          const renderedId = tmpl.content.querySelector('[data-compare-product-id]')?.dataset.compareProductId;
          if (!renderedId || renderedId !== product.id) throw new Error('Compare product mismatch');
          return { product, html };
        } finally {
          clearTimeout(timeout);
        }
      }));

      const html = [];
      const validProducts = [];
      const failedProducts = [];
      settled.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          validProducts.push(result.value.product);
          html.push(result.value.html);
        } else {
          failedProducts.push(compareProducts[index]);
        }
      });
      return { html, validProducts, failedProducts };
    }
  }

  customElements.define('compare-drawer', CompareDrawer);
}
